import { useBlockProps, InspectorControls, BlockControls, BlockAlignmentToolbar } from "@wordpress/block-editor";
import { PanelBody, SelectControl, TextControl, TextareaControl, Button, Disabled, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import { __ } from "@wordpress/i18n";

const VARIANT_OPTIONS = [
    { label: __("Compact", "content-egg"), value: "compact" },
    { label: __("Media List", "content-egg"), value: "media-list" },
    { label: __("Cards", "content-egg"), value: "cards" },
    { label: __("Featured", "content-egg"), value: "featured" },
];

const COLOR_SCHEME_OPTIONS = [
    { label: __("Auto (site default)", "content-egg"), value: "auto" },
    { label: __("Light", "content-egg"), value: "light" },
    { label: __("Dark", "content-egg"), value: "dark" },
];

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const { variant, section_label, items = [], color_scheme } = attributes;

    const updateItems = (nextItems) => setAttributes({ items: nextItems });

    const addItem = () => updateItems([...(items || []), { post_id: "", title: "", badge: "", snippet: "" }]);

    const updateItem = (index, key, value) => {
        const next = [...(items || [])];
        next[index] = { post_id: "", title: "", badge: "", snippet: "", ...(next[index] || {}), [key]: value };
        updateItems(next);
    };

    const removeItem = (index) => {
        const next = [...(items || [])];
        next.splice(index, 1);
        updateItems(next);
    };

    const hasPreviewContent = (items || []).some((item) => {
        const v = String(item?.post_id || "").trim();
        return v !== "" && v !== "0";
    });

    return (
        <>
            <BlockControls>
                <BlockAlignmentToolbar
                    value={attributes.align}
                    onChange={(val) => setAttributes({ align: val })}
                    controls={["wide", "full"]}
                />
            </BlockControls>

            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(value) => setAttributes({ variant: value })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={COLOR_SCHEME_OPTIONS}
                        onChange={(value) => setAttributes({ color_scheme: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Section Label", "content-egg")}
                        value={section_label}
                        onChange={(value) => setAttributes({ section_label: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Items", "content-egg")} initialOpen={true}>
                    {(items || []).map((item, index) => (
                        <div
                            key={index}
                            style={{
                                marginBottom: index === items.length - 1 ? "0" : "10px",
                                paddingBottom: index === items.length - 1 ? "0" : "10px",
                                borderBottom: index === items.length - 1 ? "none" : "1px solid #e0e0e0",
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                <span style={{ fontSize: "11px", fontWeight: 600, color: "#757575" }}>
                                    {`#${index + 1}`}
                                </span>
                                <Button
                                    icon="no-alt"
                                    isDestructive
                                    size="small"
                                    onClick={() => removeItem(index)}
                                    label={__("Remove", "content-egg")}
                                />
                            </div>
                            <TextControl
                                label={__("Post ID or UUID", "content-egg")}
                                value={item?.post_id || ""}
                                onChange={(value) => updateItem(index, "post_id", value.trim())}
                                placeholder={__("Post ID or node UUID", "content-egg")}
                            />
                            <TextControl
                                label={__("Title", "content-egg")}
                                value={item?.title || ""}
                                onChange={(value) => updateItem(index, "title", value)}
                                placeholder={__("Auto from post", "content-egg")}
                            />
                            <TextControl
                                label={__("Badge", "content-egg")}
                                value={item?.badge || ""}
                                onChange={(value) => updateItem(index, "badge", value)}
                                placeholder={__("Auto from category", "content-egg")}
                            />
                            <TextareaControl
                                label={__("Snippet", "content-egg")}
                                value={item?.snippet || ""}
                                onChange={(value) => updateItem(index, "snippet", value)}
                                rows={2}
                                placeholder={__("Auto from excerpt", "content-egg")}
                                __nextHasNoMarginBottom
                            />
                        </div>
                    ))}
                    <Button variant="secondary" size="small" onClick={addItem} style={{ marginTop: "10px" }}>
                        {__("Add item", "content-egg")}
                    </Button>
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {hasPreviewContent ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/related-posts"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Add related posts in the sidebar panel.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
